import { Avatar } from '@getslot/ui-web/avatar'
import { Wordmark } from '@getslot/ui-web/wordmark'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { summaryQuery } from '../api/admin'
import { meQuery } from '../api/queries'
import { RouterSideNavItem } from './links'
import { GridIcon, ListIcon, PeopleIcon, ServerIcon, ShieldIcon, SlidersIcon } from './nav-icons'
import styles from './shell.module.css'

const ROLE_LABEL = { operator: 'оператор', admin: 'админ', client: 'клиент' } as const

/** Оболочка админки: светлое меню по макету центра управления, справа экран. */
export function Shell({ children }: { children: ReactNode }) {
  const { data: me } = useSuspenseQuery(meQuery)
  // счётчик и точка в меню не должны держать экран: без Suspense
  const { data: summary } = useQuery(summaryQuery)
  const waiting = summary?.counts.needs_operator ?? 0
  const workersDown = summary?.workers.some((w) => w.health === 'error' || w.health === 'offline') ?? false

  return (
    <div className={styles.shell}>
      <aside className={styles.side}>
        <span className={styles.brand}>
          <Wordmark />
          <span className={styles.badge}>админ</span>
        </span>
        <nav aria-label="Разделы" className={styles.items}>
          <RouterSideNavItem to="/" activeOptions={{ exact: true }} icon={<GridIcon />}>
            Сводка
          </RouterSideNavItem>
          <RouterSideNavItem to="/applications" icon={<ListIcon />}>
            Заявки
            {waiting > 0 && (
              <span className={styles.count} aria-label={`ждут оператора: ${waiting}`}>
                {waiting}
              </span>
            )}
          </RouterSideNavItem>
          <RouterSideNavItem to="/clients" icon={<PeopleIcon />}>
            Клиенты
          </RouterSideNavItem>
          <RouterSideNavItem to="/workers" icon={<ServerIcon />}>
            Воркеры
            {summary !== undefined && (
              <span
                className={workersDown ? styles.dotBad : styles.dotOk}
                aria-label={workersDown ? 'есть проблемы' : 'все в порядке'}
              />
            )}
          </RouterSideNavItem>
          {me.role === 'admin' && (
            <RouterSideNavItem to="/users" icon={<ShieldIcon />}>
              Пользователи и роли
            </RouterSideNavItem>
          )}
          <RouterSideNavItem to="/settings" icon={<SlidersIcon />}>
            Настройки
          </RouterSideNavItem>
        </nav>
        <span className={styles.me}>
          <Avatar name={me.display_name} />
          <span className={styles.who}>
            <span className={styles.name}>{me.display_name ?? 'Без имени'}</span>
            <span className={styles.sub}>{ROLE_LABEL[me.role]}</span>
          </span>
        </span>
      </aside>
      <main className={styles.main}>{children}</main>
    </div>
  )
}
